import type { Money, OrderStatus } from '@loctopus/shared';
import type {
  CheckoutSession,
  EventDetail,
  Order,
  PromoCodeDiscount,
  PromoCodeValidationResult,
  SeatMap,
  Ticket,
} from './types';

/**
 * Real shape of `GET /v1/events/:id` today, confirmed live against the running API
 * (apps/api/src/routes/public/events.ts) while fixing a real crash in both apps/checkout and
 * apps/widget. It does not match this package's own `EventDetail` type: that type's doc comment
 * (types.ts) admits it was "inferred ... not a verified contract - reconcile against apps/api's
 * real responses in Step C", and that reconciliation never happened until now. Concretely:
 *  - event fields are nested under `.event`, not flat on the response root
 *  - `startsAt` lives on each session, not on the event itself
 *  - ticket types are a flat top-level array with `priceMinor`/`currency`, not a `Money` object
 *  - `addOns` and `customQuestions` are not returned by this endpoint at all (always empty here)
 * `getEvent` (client.ts) adapts every real response through this before returning it, so every
 * consumer gets a genuinely EventDetail-shaped object instead of each one needing its own
 * workaround (this used to live duplicated as apps/widget's local normalizeEvent.ts, and
 * apps/checkout had no adapter at all - it crashed on `event.addOns.length`/`event.customQuestions.length`
 * being undefined until this fix).
 */
interface RawEventResponse {
  event: {
    id: string;
    slug: string;
    title: string;
    description?: string | null;
    status: string;
    timezone: string;
    venueName?: string | null;
    venueAddress?: string | null;
    // REAL GAP FOUND AND FIXED: this interface never declared `aboutContent` at all, so even
    // though the backend now sends it (work order "Remaining items" #3), `normalizeEventDetail`
    // below silently dropped it - confirmed live (a real event page showed no About section
    // despite the field being set and visible in a direct API call).
    aboutContent?: string | null;
  };
  sessions: Array<{
    id: string;
    eventId: string;
    startsAt: string;
    endsAt: string;
    capacityTotal: number;
    status: string;
  }>;
  ticketTypes: Array<{
    id: string;
    eventId: string;
    sessionId: string | null;
    name: string;
    kind: string;
    priceMinor: number;
    currency: string;
    // REAL GAP FOUND AND FIXED: same as `aboutContent` above, for the fee-inclusive-pricing work
    // (work order "Remaining items" #4) - confirmed live (a real event page showed "$0.00 booking
    // fee" for every ticket type despite the backend correctly computing and returning 250).
    buyerFeeMinor?: number;
    capacity: number | null;
    perOrderLimit: number | null;
    saleStartsAt: string | null;
    saleEndsAt: string | null;
    isHidden: boolean;
  }>;
}

export function normalizeEventDetail(raw: unknown): EventDetail {
  const r = raw as RawEventResponse;
  const earliestStartsAt =
    r.sessions.length > 0
      ? [...r.sessions].sort((a, b) => a.startsAt.localeCompare(b.startsAt))[0]!.startsAt
      : undefined;
  const currency = (r.ticketTypes[0]?.currency ?? 'AUD') as Money['currency'];

  return {
    id: r.event.id,
    slug: r.event.slug,
    title: r.event.title,
    description: r.event.description,
    status: r.event.status as EventDetail['status'],
    timezone: r.event.timezone,
    venueName: r.event.venueName,
    venueAddress: r.event.venueAddress,
    startsAt: earliestStartsAt,
    currency,
    sessions: r.sessions.map((s) => ({
      id: s.id,
      eventId: s.eventId,
      startsAt: s.startsAt,
      endsAt: s.endsAt,
      capacityTotal: s.capacityTotal,
      status: s.status as EventDetail['sessions'][number]['status'],
    })),
    ticketTypes: r.ticketTypes.map((tt) => ({
      id: tt.id,
      eventId: tt.eventId,
      sessionId: tt.sessionId,
      name: tt.name,
      kind: tt.kind as EventDetail['ticketTypes'][number]['kind'],
      price: { amountMinor: tt.priceMinor, currency: tt.currency as Money['currency'] },
      buyerFeeMinor: tt.buyerFeeMinor,
      capacity: tt.capacity,
      perOrderLimit: tt.perOrderLimit,
      saleStartsAt: tt.saleStartsAt,
      saleEndsAt: tt.saleEndsAt,
      isHidden: tt.isHidden,
    })),
    // Real endpoint never returns these today - see this file's top comment.
    addOns: [],
    customQuestions: [],
    aboutContent: r.event.aboutContent,
  };
}

/**
 * Same reconciliation, for `GET /v1/events/:id/seat-map` (found live via the same checkout-page
 * crash this file's getEvent fix was for: "section.seats is not iterable"). The real response is
 * `{ seatMap: {...}, sections: [...], seats: [...] }` - a FLAT top-level `seats` array, not
 * nested under each section - and each seat carries `settledStatus`/`liveStatus` (the DB's
 * confirmed-vs-hold-aware view), not the single `status` field `SeatMapSeat` declares. `liveStatus`
 * is used here since it reflects in-progress holds, which is the whole point of a per-session
 * seat map query.
 */
interface RawSeatMapResponse {
  seatMap: { id: string; eventId: string; name: string; layout: Record<string, unknown> | null };
  sections: Array<{ id: string; name: string; priceZone: string; isAccessible: boolean }>;
  seats: Array<{
    id: string;
    seatSectionId: string;
    rowLabel: string;
    seatNumber: string;
    x: number | null;
    y: number | null;
    settledStatus: string;
    liveStatus: string;
    ticketTypeId?: string | null;
    price?: Money | null;
  }>;
}

export function normalizeSeatMap(raw: unknown): SeatMap {
  const r = raw as RawSeatMapResponse;
  const seatsBySection = new Map<string, RawSeatMapResponse['seats']>();
  for (const seat of r.seats) {
    const list = seatsBySection.get(seat.seatSectionId) ?? [];
    list.push(seat);
    seatsBySection.set(seat.seatSectionId, list);
  }

  return {
    id: r.seatMap.id,
    eventId: r.seatMap.eventId,
    name: r.seatMap.name,
    layout: r.seatMap.layout,
    sections: r.sections.map((section) => ({
      id: section.id,
      name: section.name,
      priceZone: section.priceZone,
      isAccessible: section.isAccessible,
      seats: (seatsBySection.get(section.id) ?? []).map((seat) => ({
        id: seat.id,
        seatSectionId: seat.seatSectionId,
        rowLabel: seat.rowLabel,
        seatNumber: seat.seatNumber,
        x: seat.x,
        y: seat.y,
        status: seat.liveStatus as SeatMap['sections'][number]['seats'][number]['status'],
        ticketTypeId: seat.ticketTypeId,
        price: seat.price,
      })),
    })),
  };
}

/**
 * Both `POST /v1/checkout-sessions` and `GET /v1/checkout-sessions/:id` return a flat
 * `{orderId, status, currency, totalMinor, ...}` shape, not the nested `CheckoutSession` this
 * package declares (`id`, Money-object `subtotal`/`discount`/`fees`/`total`) - the create route
 * also calls its provider field `provider`, while the get route calls it `paymentProvider`
 * (pre-existing inconsistency between the two routes, not introduced here; this accepts either).
 * Found live: without this, `EventPage.tsx` navigated to `checkoutSessionId: undefined` every
 * time, and no buyer purchase through the hosted Checkout app had ever been completable.
 */
interface RawCheckoutSessionResponse {
  orderId: string;
  status: string;
  currency: string;
  totalMinor: number;
  subtotal?: { amountMinor: number; currency: string };
  discount?: { amountMinor: number; currency: string };
  fees?: { amountMinor: number; currency: string };
  provider?: string;
  paymentProvider?: string;
  clientSecret?: string;
  providerData?: Record<string, unknown>;
  accessToken?: string;
}

export function normalizeCheckoutSession(raw: unknown): CheckoutSession {
  const r = raw as RawCheckoutSessionResponse;
  const currency = r.currency as Money['currency'];
  const total = { amountMinor: r.totalMinor, currency };
  return {
    id: r.orderId,
    status: r.status as CheckoutSession['status'],
    provider: (r.provider ?? r.paymentProvider) as CheckoutSession['provider'],
    currency,
    // Not every call site of this endpoint computes/returns a breakdown (e.g. an order created
    // before this normalization existed, or a provider error path) - total is always known
    // (it's the one field both routes always send), subtotal/discount/fees fall back to it/zero
    // only when genuinely absent, never silently misreported as a real computed value.
    subtotal: r.subtotal ? { amountMinor: r.subtotal.amountMinor, currency } : total,
    discount: r.discount ? { amountMinor: r.discount.amountMinor, currency } : { amountMinor: 0, currency },
    fees: r.fees ? { amountMinor: r.fees.amountMinor, currency } : { amountMinor: 0, currency },
    total,
    clientSecret: r.clientSecret,
    providerData: r.providerData,
    accessToken: r.accessToken,
  };
}

/**
 * Real shape of `GET /v1/orders/:id` (apps/storefront-api and apps/public-api's
 * `routes/public/orders.ts`, confirmed by reading the route and the `orders`/`tickets` table
 * schemas directly, not inferred): `{ order: {...row}, items: [...row], tickets: [...row +
 * accessToken] }` - fields nested under `.order`/snake_case-derived columns, not the flat,
 * Money-object `Order` shape this package declares. `getOrder` never had a normalizer at all
 * until this fix (unlike `getCheckoutSession`/`getEvent` above) - found while fixing the real
 * guest-buyer-confirmation-page bug (`client.ts`'s `getOrder`/`getTicket` never forwarded
 * `accessToken`): forwarding the token alone was not sufficient, since every field this type's
 * consumers read (`order.status`, `order.ticketIds`, ...) would still have been `undefined`
 * against the real nested response.
 */
interface RawOrderResponse {
  order: {
    id: string;
    eventId: string;
    status: string;
    buyerEmail: string;
    buyerPhone?: string | null;
    currency: string;
    subtotalMinor: number;
    feesMinor: number;
    discountMinor: number;
    totalMinor: number;
    feePayer: string;
    paymentProvider?: string | null;
    promoCodeId?: string | null;
    createdAt: string;
    paidAt?: string | null;
  };
  items: Array<{
    id: string;
    orderId: string;
    ticketTypeId?: string | null;
    addOnId?: string | null;
    seatId?: string | null;
    quantity: number;
    unitPriceMinor: number;
    amountMinor: number;
    currency: string;
  }>;
  tickets: Array<{
    id: string;
    orderId: string;
    ticketTypeId: string;
    sessionId: string;
    seatId?: string | null;
    status: string;
    holderName?: string | null;
    holderEmail?: string | null;
    checkedInAt?: string | null;
    transferredToTicketId?: string | null;
    createdAt: string;
    accessToken: string;
  }>;
}

export function normalizeOrder(raw: unknown): Order {
  const r = raw as RawOrderResponse;
  const currency = r.order.currency as Money['currency'];
  return {
    id: r.order.id,
    eventId: r.order.eventId,
    status: r.order.status as OrderStatus,
    buyer: { email: r.order.buyerEmail, phone: r.order.buyerPhone ?? undefined },
    items: r.items.map((i) => ({
      id: i.id,
      orderId: i.orderId,
      ticketTypeId: i.ticketTypeId,
      addOnId: i.addOnId,
      seatId: i.seatId,
      quantity: i.quantity,
      unitPrice: { amountMinor: i.unitPriceMinor, currency: i.currency as Money['currency'] },
      amount: { amountMinor: i.amountMinor, currency: i.currency as Money['currency'] },
    })),
    // Real route never returns order_answers today (confirmed by reading routes/public/orders.ts)
    // - always empty here, not faked.
    answers: [],
    currency,
    subtotal: { amountMinor: r.order.subtotalMinor, currency },
    fees: { amountMinor: r.order.feesMinor, currency },
    discount: { amountMinor: r.order.discountMinor, currency },
    total: { amountMinor: r.order.totalMinor, currency },
    feePayer: r.order.feePayer as Order['feePayer'],
    paymentProvider: r.order.paymentProvider as Order['paymentProvider'],
    promoCode: r.order.promoCodeId,
    ticketIds: r.tickets.map((t) => t.id),
    ticketAccessTokens: Object.fromEntries(r.tickets.map((t) => [t.id, t.accessToken])),
    createdAt: r.order.createdAt,
    paidAt: r.order.paidAt,
  };
}

/**
 * Real shape of `GET /v1/tickets/:id` (apps/storefront-api and apps/public-api's
 * `routes/public/tickets.ts`): `{ ticket: {...row}, qr: { svg: "<svg>...</svg>" } }` - nested
 * under `.ticket`, and the QR code is returned as inline SVG markup, not a URL. This package's
 * `Ticket.qrCodeUrl` is honored as a genuinely loadable URL (an `<img src>` can use it directly)
 * by wrapping the SVG markup in a `data:` URL, rather than changing that field's documented
 * contract.
 */
interface RawTicketResponse {
  ticket: {
    id: string;
    orderId: string;
    ticketTypeId: string;
    sessionId: string;
    seatId?: string | null;
    status: string;
    holderName?: string | null;
    holderEmail?: string | null;
    checkedInAt?: string | null;
    transferredToTicketId?: string | null;
    createdAt: string;
  };
  qr: { svg: string };
}

export function normalizeTicket(raw: unknown): Ticket {
  const r = raw as RawTicketResponse;
  return {
    id: r.ticket.id,
    orderId: r.ticket.orderId,
    ticketTypeId: r.ticket.ticketTypeId,
    sessionId: r.ticket.sessionId,
    seatId: r.ticket.seatId,
    status: r.ticket.status as Ticket['status'],
    holderName: r.ticket.holderName,
    holderEmail: r.ticket.holderEmail,
    checkedInAt: r.ticket.checkedInAt,
    transferredToTicketId: r.ticket.transferredToTicketId,
    qrCodeUrl: `data:image/svg+xml;utf8,${encodeURIComponent(r.qr.svg)}`,
    createdAt: r.ticket.createdAt,
  };
}

/**
 * Real shape of `POST /v1/promo-codes/validate` today, confirmed live against
 * apps/storefront-api/src/routes/public/promoCodes.ts. It does not match this package's
 * `PromoCodeValidationResult`/`PromoCodeDiscount` types at all: the invalid-code explanation is
 * `reason`, not `message`, and there is no `discount.kind` tagged union - a resolved
 * `discount: Money | null` (only ever non-null for a `fixed` code; always `null` for `percent`
 * since this endpoint never passes a subtotal to resolve it against) and a separate raw `promo`
 * (id/type/percentBps/fixedAmountMinor) are returned instead.
 *
 * REAL BUG FOUND AND FIXED: `validatePromoCode` (client.ts) returned this raw shape completely
 * unnormalized, so every caller's `result.discount.kind` check was always reading `undefined`
 * off a plain Money-or-null value - apps/checkout's EventPage.tsx was already written against
 * this package's documented `PromoCodeDiscount` shape (its `estimatedTotal` memo branches on
 * `.kind === 'percent' | 'fixed'`), so that branch never ran for ANY promo code, live or not.
 * Confirmed live: applying a real 100%-off code left the on-page total completely unchanged.
 */
interface RawPromoCodeValidationResponse {
  valid: boolean;
  discount: { amountMinor: number; currency: string } | null;
  reason?: string;
  promo?: {
    id: string;
    type: 'percent' | 'fixed' | 'access' | 'bundle';
    percentBps: number | null;
    fixedAmountMinor: number | null;
    bundleMinQty: number | null;
    bundleFreeQty: number | null;
  };
}

export function normalizePromoCodeValidation(raw: unknown, code: string): PromoCodeValidationResult {
  const r = raw as RawPromoCodeValidationResponse;
  if (!r.valid || !r.promo) {
    return { valid: false, code, message: r.reason };
  }
  // `fixedAmountMinor` has no currency of its own on `promo` - `r.discount` (only ever resolved
  // for type "fixed", see doc comment above) is the one place a real currency comes back; a
  // percent code has no money amount to take a currency from at all, so it's irrelevant there.
  const currency = (r.discount?.currency ?? 'AUD') as Money['currency'];
  let discount: PromoCodeDiscount;
  if (r.promo.type === 'access') {
    discount = { kind: 'access' };
  } else if (r.promo.type === 'percent') {
    discount = { kind: 'percent', percentBps: r.promo.percentBps ?? 0 };
  } else if (r.promo.type === 'bundle') {
    discount = { kind: 'bundle', minQty: r.promo.bundleMinQty ?? 0, freeQty: r.promo.bundleFreeQty ?? 0 };
  } else {
    discount = { kind: 'fixed', amount: { amountMinor: r.promo.fixedAmountMinor ?? 0, currency } };
  }
  return { valid: true, code, discount };
}
